import { useMemo } from 'react';
import { TripResponse } from '../../store/backendApi';

interface UseTripNavigationReturn {
  viewTripUrl: string;
  getViewTripUrl: () => string;
}

/**
 * Hook to generate trip view URLs based on share mode.
 * Platform-agnostic: URL generation logic, caller handles actual navigation.
 *
 * @param trip - The trip data containing shareMode, slug, linkSecret
 * @param username - The current user's username
 * @param tripId - The trip ID (fallback)
 * @returns View trip URL and getter function
 */
export const useTripNavigation = (
  trip?: TripResponse,
  username?: string,
  tripId?: string,
): UseTripNavigationReturn => {
  const viewTripUrl = useMemo(() => {
    if (!trip) return '';

    switch (trip.shareMode) {
      case 'PUBLIC':
        // Public URL: /trip/{username}/{slug}
        if (username && trip.slug) {
          return `/trip/${username}/${trip.slug}/`;
        }
        // Fallback to private URL
        return `/p/trip/${tripId}/`;

      case 'LINK':
        // Link secret URL: /trip/{linkSecret}
        if (trip.linkSecret) {
          return `/trip/${trip.linkSecret}/`;
        }
        // Fallback to private URL
        return `/p/trip/${tripId}/`;

      case 'PRIVATE':
      default:
        // Private URL: /p/trip/{tripId}
        return `/p/trip/${tripId}/`;
    }
  }, [trip, username, tripId]);

  const getViewTripUrl = () => viewTripUrl;

  return {
    viewTripUrl,
    getViewTripUrl,
  };
};
